/**
 * Starter venues.
 *
 * Six plans that between them cover the shapes of movement the product is for:
 * a queue that backs up, a crowd that disperses, people who sit and stay, a
 * hall with counterflow, and a room under evacuation. They exist so that the
 * first thing anyone sees is a venue with people already moving through it
 * rather than an empty grid — and so every feature has something to act on
 * before the user has drawn anything.
 */

import type { CrowdDocument, Population, Scenario } from '../core/model/types'
import {
  AGENT_PROFILES,
  createDocument,
  DEFAULT_PROFILE_MIX,
  POPULATION_COLORS,
} from '../core/model/defaults'
import { newId } from '../core/model/ids'
import { feet, inches } from '../core/model/standards'
import { PlanBuilder, step } from './planBuilder'

export interface Template {
  id: string
  name: string
  summary: string
  /** What this venue is useful for demonstrating. */
  teaches: string
  build: () => CrowdDocument
}

const makeDocument = (
  name: string,
  plan: ReturnType<PlanBuilder['build']>,
  scenario: Omit<Scenario, 'profiles'>,
): CrowdDocument => ({
  ...createDocument(name),
  plan,
  scenario: { ...scenario, profiles: AGENT_PROFILES.map((p) => ({ ...p })) },
})

const population = (
  overrides: Partial<Population> & Pick<Population, 'name' | 'count' | 'entryIds' | 'itinerary'>,
): Population => ({
  id: newId('pop'),
  color: POPULATION_COLORS[0],
  arrival: { kind: 'uniform', startS: 0, windowS: 600 },
  profileMix: DEFAULT_PROFILE_MIX.map((entry) => ({ ...entry })),
  ...overrides,
})

const baseRouting: Scenario['routing'] = {
  adaptive: true,
  congestionWeight: 0.55,
  replanIntervalS: 2,
  routeVariety: 0.3,
}

// --- 1. Coffee bar -----------------------------------------------------------

const coffeeBar = (): CrowdDocument => {
  const b = new PlanBuilder()
  const room = b.room(0, 0, feet(46), feet(33), { height: feet(10) })
  const street = b.door(room.south, 2.0, feet(6), 'door', 'both')
  // At tables and chairs this floor holds 102, and above 49 the code wants a
  // second way out. With the street door alone the first venue anybody opened
  // failed its own code check.
  b.door(room.east, 5.0, feet(3), 'door', 'exit')
  b.window(room.west, 3, feet(8))
  b.window(room.west, 7, feet(8))
  b.window(room.north, 5, feet(8))

  const counter = b.service(
    'Coffee bar',
    11.2,
    7.6,
    Math.PI,
    2,
    {
      kind: 'lognormal',
      mean: 42,
      sd: 16,
      min: 12,
    },
    {
      width: 3.2,
      depth: 0.8,
      queue: [
        { x: 11.2, y: 6.4 },
        { x: 11.2, y: 3.2 },
        { x: 7.6, y: 3.2 },
      ],
    },
  )
  b.place('coffee-station', 12.6, 9.2, Math.PI)
  b.place('shelving', 8.6, 9.5, Math.PI)

  b.tableWithChairs('table-square-4', 3.0, 7.4)
  b.tableWithChairs('table-square-4', 6.0, 7.4)
  b.tableWithChairs('table-round-4', 3.0, 4.4)
  b.tableWithChairs('table-round-4', 6.0, 4.4)
  b.place('sofa-2', 1.6, 1.9, Math.PI / 2)
  b.place('table-coffee', 2.9, 1.9, Math.PI / 2)
  b.place('plant-tree', 12.8, 1.4)
  b.place('plant-small', 0.9, 9.0)
  b.place('bin', 8.0, 0.8)

  const seating = b.zone('seating', 1.2, 3.2, 8.0, 9.0, 'Seating area')
  b.zone('keep-clear', 8.6, 1.0, 10.4, 9.0, 'Service aisle', { cost: 3 })

  return makeDocument('Coffee bar', b.build(), {
    name: 'Morning rush',
    durationS: 2400,
    seed: 1,
    speedFactor: 1,
    routing: baseRouting,
    evacuationAtS: null,
    populations: [
      population({
        name: 'Customers',
        count: 90,
        color: POPULATION_COLORS[0],
        entryIds: [street.id],
        arrival: { kind: 'peak', startS: 0, windowS: 1800, peakAt: 0.35, spread: 0.16 },
        itinerary: [
          step('service', counter.id),
          step('seat', seating.id, {
            duration: { kind: 'lognormal', mean: 720, sd: 300, min: 180 },
            probability: 0.55,
          }),
          step('exit', street.id),
        ],
      }),
    ],
  })
}

// --- 2. Conference registration ---------------------------------------------

const conference = (): CrowdDocument => {
  const b = new PlanBuilder()
  const hall = b.room(0, 0, feet(99), feet(86), { height: feet(14) })
  const mainDoor = b.door(hall.south, 4, feet(8), 'door', 'both')
  const sideDoor = b.door(hall.south, 12, feet(8), 'door', 'both')
  // The session room's own way out, beside the east block. The hall holds 568
  // at tables and chairs, which needs three exits, and it had the two doors
  // the delegates come in by.
  b.door(hall.east, 17.2, feet(6), 'door', 'exit')
  // Partition between the foyer and the session room.
  const partition = b.wall(
    { x: 0, y: 12 },
    { x: 30, y: 12 },
    { kind: 'partition', thickness: inches(4.5) },
  )
  b.door(partition, 8, feet(8))
  b.door(partition, 22, feet(8))

  const desks = [6, 11, 16, 21].map((x, index) =>
    b.service(
      `Registration ${index + 1}`,
      x,
      10.6,
      Math.PI,
      1,
      { kind: 'lognormal', mean: 38, sd: 14, min: 10 },
      {
        width: 2.0,
        depth: 0.8,
        queue: [
          { x, y: 9.4 },
          { x, y: 4.2 },
        ],
      },
    ),
  )
  for (const x of [6, 11, 16, 21]) b.place('counter-reception', x, 10.6, Math.PI)
  b.place('banner', 2.0, 10.4, Math.PI)
  b.place('banner', 26.0, 10.4, Math.PI)
  b.place('coat-rail', 27.5, 6.0, Math.PI / 2)
  b.place('coat-rail', 27.5, 4.0, Math.PI / 2)
  b.place('plant-tree', 1.4, 6.0)
  b.place('plant-tree', 28.4, 9.6)

  // Session room: 360 seats for the 320 delegates, in two blocks of 20 either
  // side of the centre aisle. It held 210, and 42 of those were under the
  // stage, so half the delegates had no seat and stood among the full rows
  // until they gave up.
  b.place('stage', 15, 23.9, 0, { size: { width: 10, depth: 2.6, height: 0.5 } })
  // 16 ft wide, 16:9, and it has to fit under a 14 ft ceiling: this one used
  // to be 3.4 m tall in a 3.2 m room.
  b.place('projector-screen', 15, 24.9, 0, {
    size: { width: feet(16), depth: 0.2, height: feet(9) },
  })
  b.place('lectern', 19.5, 23.1)
  // Unturned, a row faces north, at the stage. Turned half round, the
  // delegates sat with their backs to it, facing the doors.
  const rowWidth = 11
  const [westBlock, eastBlock] = [7.8, 22.2]
  for (let row = 0; row < 9; row++) {
    for (const x of [westBlock, eastBlock]) {
      b.place('seat-row', x, 13.4 + row * 0.95, 0, {
        size: { width: rowWidth, depth: 0.7, height: 0.95 },
      })
    }
  }

  const session = b.zone('seating', 1.8, 12.8, 28.2, 21.8, 'Session room')
  // The aisle runs from row end to row end. Stopped 0.3 m short of both, it
  // left a strip of open floor one cell wide between the chairs and the zone,
  // cheaper than either, and the route out of every row ran down it. A strip
  // narrower than a body has no floor to walk along, and one delegate leaving
  // by it stepped from one side of it to the other for twenty minutes.
  b.zone(
    'keep-clear',
    westBlock + rowWidth / 2,
    12.4,
    eastBlock - rowWidth / 2,
    22.2,
    'Centre aisle',
    { cost: 5 },
  )
  b.zone('measure', 0.4, 12.2, 29.6, 13.0, 'Session doorway')

  return makeDocument('Conference registration', b.build(), {
    name: 'Doors open',
    durationS: 3600,
    seed: 1,
    speedFactor: 1,
    routing: baseRouting,
    evacuationAtS: null,
    populations: [
      population({
        name: 'Delegates',
        count: 320,
        color: POPULATION_COLORS[0],
        entryIds: [mainDoor.id, sideDoor.id],
        arrival: { kind: 'peak', startS: 0, windowS: 1800, peakAt: 0.55, spread: 0.2 },
        groupSize: { min: 1, max: 3 },
        itinerary: [
          step('service', undefined, { targetIds: desks.map((d) => d.id) }),
          step('seat', session.id, { duration: { kind: 'normal', mean: 1500, sd: 200, min: 600 } }),
          step('exit', undefined, { targetIds: [mainDoor.id, sideDoor.id] }),
        ],
      }),
    ],
  })
}

// --- 3. Gallery opening ------------------------------------------------------

const gallery = (): CrowdDocument => {
  const b = new PlanBuilder()
  const room = b.room(0, 0, feet(79), feet(53), { height: feet(12) })
  const frontDoor = b.door(room.south, 3, feet(8), 'door', 'both')
  // 280 at tables and chairs need a second exit, and this door was drawn
  // without being one.
  b.door(room.east, 8, feet(5), 'door', 'exit')
  // Interior partitions that make a route rather than one big box.
  const p1 = b.wall(
    { x: 8, y: 0.2 },
    { x: 8, y: 9 },
    { kind: 'partition', thickness: inches(8), height: feet(10) },
  )
  const p2 = b.wall(
    { x: 16, y: 7 },
    { x: 16, y: 15.8 },
    { kind: 'partition', thickness: inches(8), height: feet(10) },
  )
  b.door(p1, 5.5, feet(7), 'opening')
  b.door(p2, 4.0, feet(7), 'opening')

  for (const [x, y, rot] of [
    [3, 15.7, 0],
    [6, 15.7, 0],
    [12, 15.7, 0],
    [20, 15.7, 0],
    [7.7, 3, Math.PI / 2],
    [7.7, 6.5, Math.PI / 2],
    [16.3, 10, -Math.PI / 2],
    [16.3, 13.5, -Math.PI / 2],
    [23.7, 4, -Math.PI / 2],
    [23.7, 11, -Math.PI / 2],
  ] as Array<[number, number, number]>) {
    b.place('artwork', x, y, rot)
  }
  for (const at of [
    { x: 12, y: 8 },
    { x: 18, y: 4 },
  ]) {
    // A column runs floor to ceiling; the catalog cannot know which ceiling.
    b.place('column-round', at.x, at.y, 0, {
      size: { width: 0.5, depth: 0.5, height: feet(12) },
    })
  }

  const bar = b.service(
    'Drinks bar',
    21.4,
    14.8,
    Math.PI,
    2,
    { kind: 'lognormal', mean: 28, sd: 10, min: 8 },
    {
      width: 3.0,
      depth: 0.8,
      queue: [
        { x: 21.4, y: 13.6 },
        { x: 21.4, y: 10.0 },
      ],
    },
  )
  b.place('counter-bar', 21.4, 14.8, Math.PI, { size: { width: 3.0, depth: 0.7, height: 1.1 } })
  for (const x of [18.0, 19.4, 20.8]) b.place('table-poseur', x, 8.6)
  b.place('plant-tree', 1.6, 8.0)

  const westWing = b.zone('waypoint', 0.6, 9.5, 7.4, 15.2, 'West wall', {
    dwell: { kind: 'lognormal', mean: 210, sd: 90, min: 45 },
  })
  const eastWing = b.zone('waypoint', 9.0, 1.0, 15.2, 6.4, 'East wall', {
    dwell: { kind: 'lognormal', mean: 210, sd: 90, min: 45 },
  })
  const backWing = b.zone('waypoint', 17.0, 8.0, 23.4, 14.0, 'Back room', {
    dwell: { kind: 'lognormal', mean: 180, sd: 80, min: 40 },
  })

  return makeDocument('Gallery opening', b.build(), {
    name: 'Private view',
    durationS: 5400,
    seed: 1,
    speedFactor: 1,
    routing: { ...baseRouting, routeVariety: 0.5 },
    evacuationAtS: null,
    populations: [
      population({
        name: 'Guests',
        count: 180,
        color: POPULATION_COLORS[4],
        entryIds: [frontDoor.id],
        arrival: { kind: 'peak', startS: 120, windowS: 2700, peakAt: 0.3, spread: 0.22 },
        groupSize: { min: 1, max: 4 },
        itinerary: [
          step('service', bar.id, { probability: 0.8 }),
          step('dwell', westWing.id, { probability: 0.7 }),
          step('dwell', eastWing.id, { probability: 0.8 }),
          step('dwell', backWing.id, { probability: 0.6 }),
          step('exit', frontDoor.id),
        ],
      }),
    ],
  })
}

// --- 4. Polling station ------------------------------------------------------

const pollingStation = (): CrowdDocument => {
  const b = new PlanBuilder()
  const room = b.room(0, 0, feet(60), feet(40), { height: feet(10) })
  // The way in is a way out too. Marked for entry only, it left one exit for
  // a room the code wants two in.
  const wayIn = b.door(room.south, 3, feet(6), 'door', 'both')
  const wayOut = b.door(room.north, 15, feet(6), 'door', 'exit')

  // Four poll-book stations, because two cannot keep up with the door: voters
  // arrive at about 2.9 a minute averaged over the session and a station clears
  // one every 55 seconds, so at two the queue grows without bound and the venue
  // demonstrates nothing but its own arithmetic. Four is also what a precinct
  // this size actually staffs.
  const checkIn = [3.0, 5.2, 7.4, 9.6].map((x, index) =>
    b.service(
      `Check-in ${index + 1}`,
      x,
      9.6,
      Math.PI,
      1,
      { kind: 'lognormal', mean: 55, sd: 20, min: 20 },
      {
        width: 1.8,
        depth: 0.8,
        queue: [
          { x, y: 8.4 },
          { x, y: 3.4 },
        ],
      },
    ),
  )
  for (const x of [3.0, 5.2, 7.4, 9.6]) b.place('counter-reception', x, 9.6, Math.PI)
  for (const x of [12.5, 14.0, 15.5]) {
    b.place('ballot-booth', x, 9.4, Math.PI)
    b.place('ballot-booth', x, 6.4, Math.PI)
  }
  b.place('bin', 17.0, 1.0)
  b.place('floor-sign', 3.4, 2.6, Math.PI / 4)
  // Two barriers end to end. One is 2 m; 3.2 m is one and a half of them.
  for (const y of [3.6, 5.6]) {
    b.place('barrier', 11.0, y, Math.PI / 2, {
      size: { width: 2.0, depth: 0.12, height: 1.1 },
    })
  }

  const booths = b.zone('waypoint', 11.4, 5.4, 16.6, 10.4, 'Voting booths', {
    dwell: { kind: 'lognormal', mean: 110, sd: 45, min: 35 },
    capacity: 6,
  })

  return makeDocument('Polling station', b.build(), {
    name: 'Evening peak',
    durationS: 5400,
    seed: 1,
    speedFactor: 1,
    routing: baseRouting,
    evacuationAtS: null,
    populations: [
      population({
        name: 'Voters',
        count: 260,
        color: POPULATION_COLORS[2],
        entryIds: [wayIn.id],
        arrival: { kind: 'peak', startS: 0, windowS: 5400, peakAt: 0.7, spread: 0.15 },
        profileMix: [
          { profileId: 'adult', weight: 50 },
          { profileId: 'senior', weight: 28 },
          { profileId: 'hurried', weight: 12 },
          { profileId: 'wheelchair', weight: 6 },
          { profileId: 'child', weight: 4 },
        ],
        itinerary: [
          step('service', undefined, { targetIds: checkIn.map((d) => d.id) }),
          step('dwell', booths.id),
          step('exit', wayOut.id),
        ],
      }),
    ],
  })
}

// --- 5. Transit concourse ----------------------------------------------------

const concourse = (): CrowdDocument => {
  const b = new PlanBuilder()
  const hall = b.room(0, 0, feet(132), feet(60), { height: feet(16) })
  // A concourse's ways in and out are its portals, and people use them both
  // directions all day, so each is marked for both.
  const westPortals = [6, 12].map((y) => b.door(hall.west, y, feet(10), 'opening', 'both'))
  const platformPortals = [6, 12].map((y) => b.door(hall.east, y, feet(10), 'opening', 'both'))
  const streetStair = b.door(hall.south, 20, feet(12), 'opening', 'entry')

  for (const x of [9, 11, 13]) b.place('kiosk', x, 16.6, Math.PI)
  for (const x of [26, 27.4, 28.8, 30.2]) b.place('turnstile', x, 9, 0)
  b.place('shelving', 20, 16.8, Math.PI, { size: { width: 4, depth: 0.6, height: 2.0 } })
  b.place('bench', 5, 3.0, 0, { size: { width: 2.4, depth: 0.45, height: 0.45 } })
  b.place('bench', 8, 3.0, 0, { size: { width: 2.4, depth: 0.45, height: 0.45 } })
  b.place('screen-tv', 20, 2.0, 0, { size: { width: 2.4, depth: 0.3, height: 2.4 } })
  for (const x of [15, 25]) {
    b.place('column-square', x, 9, 0, {
      size: { width: 0.6, depth: 0.6, height: feet(16) },
    })
  }
  b.place('bin', 35, 2)
  b.place('plant-tree', 34, 16)

  // The gate is the turnstile bank itself. A counter drawn behind it put the
  // place each passenger stands to tap in inside a turnstile.
  const gate = b.service(
    'Ticket gate',
    28,
    9,
    Math.PI,
    4,
    { kind: 'lognormal', mean: 4.5, sd: 1.6, min: 1.5 },
    {
      width: 5.2,
      depth: 1.2,
      queueSpacing: 0.55,
      queue: [
        { x: 28, y: 7.5 },
        { x: 28, y: 4.0 },
      ],
    },
  )

  b.zone('measure', 24.0, 7.0, 32.0, 11.0, 'Gate line')
  b.zone('keep-clear', 14.0, 7.5, 16.0, 10.5, 'Around column', { cost: 3 })

  return makeDocument('Transit concourse', b.build(), {
    name: 'Peak hour, both directions',
    durationS: 1800,
    seed: 1,
    speedFactor: 1,
    routing: { ...baseRouting, congestionWeight: 0.7, routeVariety: 0.35 },
    evacuationAtS: null,
    populations: [
      population({
        name: 'Boarding',
        count: 420,
        color: POPULATION_COLORS[0],
        entryIds: [...westPortals.map((d) => d.id), streetStair.id],
        arrival: { kind: 'poisson', startS: 0, windowS: 1500 },
        profileMix: [
          { profileId: 'adult', weight: 46 },
          { profileId: 'hurried', weight: 30 },
          { profileId: 'luggage', weight: 14 },
          { profileId: 'senior', weight: 6 },
          { profileId: 'child', weight: 3 },
          { profileId: 'wheelchair', weight: 1 },
        ],
        itinerary: [
          step('service', gate.id),
          step('exit', undefined, { targetIds: platformPortals.map((d) => d.id) }),
        ],
      }),
      population({
        name: 'Arriving',
        count: 260,
        color: POPULATION_COLORS[1],
        entryIds: platformPortals.map((d) => d.id),
        arrival: { kind: 'waves', startS: 90, windowS: 1400, waves: 7 },
        itinerary: [step('exit', undefined, { targetIds: westPortals.map((d) => d.id) })],
      }),
    ],
  })
}

// --- 6. Banquet hall ---------------------------------------------------------

const banquet = (): CrowdDocument => {
  const b = new PlanBuilder()
  const hall = b.room(0, 0, feet(92), feet(66), { height: feet(16) })
  const mainDoors = b.door(hall.south, 6, feet(8), 'door', 'both')
  const secondDoors = b.door(hall.south, 22, feet(8), 'door', 'both')
  b.door(hall.east, 10, feet(5), 'door', 'exit')

  b.place('stage', 14, 18.2, 0, { size: { width: 9, depth: 3.0, height: 0.6 } })
  b.place('lectern', 17.5, 18.4)
  b.place('projector-screen', 14, 19.5, 0, {
    size: { width: feet(18), depth: 0.2, height: feet(10, 2) },
  })

  // Ten banquet rounds of eight with a clear centre aisle. The floor stops short
  // of the top row's back chairs, which face the counters: offered, the guests
  // making for them jammed the front of the buffet queue.
  const seatingZone = b.zone('seating', 1.5, 3.0, 26.5, 15.5, 'Dining floor')
  for (let row = 0; row < 3; row++) {
    for (let column = 0; column < 4; column++) {
      const x = 4.2 + column * 6.6
      const y = 5.0 + row * 4.8
      if (row === 1 && (column === 1 || column === 2)) continue
      b.tableWithChairs('table-round-8', x, y, 0, 'chair-stacking')
    }
  }

  const bar = b.service(
    'Bar',
    2.4,
    17.6,
    Math.PI,
    2,
    { kind: 'lognormal', mean: 34, sd: 12, min: 10 },
    {
      width: 3.4,
      depth: 0.8,
      queue: [
        { x: 1.2, y: 16.4 },
        { x: 1.2, y: 12.4 },
      ],
    },
  )
  b.place('counter-bar', 2.4, 17.6, Math.PI, { size: { width: 3.4, depth: 0.7, height: 1.1 } })
  const buffet = b.service(
    'Buffet',
    25.6,
    17.6,
    Math.PI,
    3,
    { kind: 'lognormal', mean: 46, sd: 15, min: 18 },
    {
      width: 4.0,
      depth: 0.9,
      queue: [
        { x: 26.8, y: 16.2 },
        { x: 26.8, y: 11.0 },
      ],
    },
  )
  b.place('counter-buffet', 25.6, 17.6, Math.PI, { size: { width: 4.0, depth: 0.8, height: 0.9 } })
  b.place('plant-tree', 13.5, 10.0)

  const staffStation = b.zone('waypoint', 6.0, 17.0, 11.0, 19.4, 'Service station', {
    dwell: { kind: 'uniform', mean: 300, min: 120, max: 600 },
  })
  b.zone('keep-clear', 12.6, 2.0, 15.6, 16.0, 'Service aisle', { cost: 4 })

  return makeDocument('Banquet hall', b.build(), {
    name: 'Reception, dinner, and a fire drill',
    durationS: 5400,
    seed: 1,
    speedFactor: 1,
    routing: baseRouting,
    evacuationAtS: 3600,
    populations: [
      population({
        name: 'Guests',
        count: 220,
        color: POPULATION_COLORS[3],
        entryIds: [mainDoors.id, secondDoors.id],
        arrival: { kind: 'peak', startS: 0, windowS: 1800, peakAt: 0.45, spread: 0.2 },
        groupSize: { min: 2, max: 5 },
        itinerary: [
          step('service', bar.id, { probability: 0.75 }),
          step('service', buffet.id),
          step('seat', seatingZone.id, {
            duration: { kind: 'normal', mean: 2400, sd: 400, min: 900 },
          }),
          step('exit', undefined, { targetIds: [mainDoors.id, secondDoors.id] }),
        ],
      }),
      population({
        name: 'Staff',
        count: 14,
        color: POPULATION_COLORS[5],
        entryIds: [secondDoors.id],
        arrival: { kind: 'all-at-once', startS: 0, windowS: 0 },
        profileMix: [{ profileId: 'staff', weight: 1 }],
        itinerary: [
          step('dwell', staffStation.id, {
            duration: { kind: 'uniform', mean: 240, min: 90, max: 480 },
          }),
          step('goto', seatingZone.id),
          step('dwell', staffStation.id, {
            duration: { kind: 'uniform', mean: 240, min: 90, max: 480 },
          }),
          step('exit', secondDoors.id),
        ],
      }),
    ],
  })
}

export const TEMPLATES: Template[] = [
  {
    id: 'coffee-bar',
    name: 'Coffee bar',
    summary: 'A 14 × 10 m café with a two-server bar and a queue that folds around the room.',
    teaches: 'How a single counter becomes the constraint, and what a third barista buys you.',
    build: coffeeBar,
  },
  {
    id: 'conference',
    name: 'Conference registration',
    summary: 'A 30 × 20 m foyer with four registration desks feeding a 300-seat session room.',
    teaches:
      'Queue balancing across parallel desks, and the doorway surge when the session starts.',
    build: conference,
  },
  {
    id: 'gallery',
    name: 'Gallery opening',
    summary: 'A partitioned gallery with a drinks bar and three hanging walls to work around.',
    teaches: 'Dwell behaviour, route variety, and where a crowd pools when nobody is in a hurry.',
    build: gallery,
  },
  {
    id: 'polling-station',
    name: 'Polling station',
    summary: 'Two check-in desks, six booths, and an accessible route from door to exit.',
    teaches: 'Service capacity against an evening peak, with an older and less mobile population.',
    build: pollingStation,
  },
  {
    id: 'concourse',
    name: 'Transit concourse',
    summary: 'A 40 × 18 m hall with four ticket gates and trains arriving in waves.',
    teaches: 'Counterflow, lane formation, and gate throughput under a wave of arrivals.',
    build: concourse,
  },
  {
    id: 'banquet',
    name: 'Banquet hall',
    summary: 'Ten banquet rounds, a bar, a buffet, and an unannounced evacuation at one hour.',
    teaches: 'Seated occupancy, buffet queueing, and how long a seated room takes to clear.',
    build: banquet,
  },
]

export const getTemplate = (id: string): Template | undefined => TEMPLATES.find((t) => t.id === id)

export const DEFAULT_TEMPLATE_ID = 'coffee-bar'
